<template>
  <div id="account-select-container" class="container">
    <div class="wrap">

      <!-- Header -->
      <div class="intro-section">
        <div class="intro-badge">WYBÓR KONTA</div>
        <div class="header-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <path d="M2 10h20"/>
          </svg>
        </div>
        <h2 class="intro-title">Wybierz rachunek do wypłaty</h2>
        <p class="intro-text">Na wybrany rachunek przelejemy kwotę Twojej pożyczki.</p>
      </div>

      <!-- Podsumowanie pożyczki -->
      <div class="loan-summary-card">
        <div class="summary-row">
          <span class="summary-label">Kwota pożyczki</span>
          <span class="summary-value highlight">{{ formatCurrency(loanAmount) }} zł</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Okres</span>
          <span class="summary-value">{{ loanTenor }} {{ tenorSign == 'd' ? 'dni' : 'mc' }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Data spłaty</span>
          <span class="summary-value">{{ finishDate }}</span>
        </div>
      </div>

      <!-- Lista kont -->
      <div class="account-section">
        <div class="section-header">
          <div class="section-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
          </div>
          <div>
            <h3>Dostępne rachunki bankowe</h3>
            <p>Wybierz konto, na które chcesz otrzymać środki</p>
          </div>
        </div>

        <!-- Loading state: pierwsze pobranie albo oczekiwanie na dane transakcyjne z EasyCheck -->
        <div v-if="isAccountsLoading" class="accounts-loading" aria-live="polite">
          <div class="loading-spinner">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <circle cx="12" cy="12" r="10" opacity="0.25"/>
              <path d="M12 2a10 10 0 0110 10" stroke-linecap="round"/>
            </svg>
          </div>
          <span v-if="accountsTries <= 1">Pobieranie listy kont...</span>
          <template v-else>
            <span class="accounts-waiting-title">Czekamy na dane transakcyjne z Twojego banku…</span>
            <span class="accounts-waiting-hint">Dane z banku nie zostały jeszcze pobrane. Sprawdzamy ponownie co kilka sekund
              &mdash; prosimy o pozostanie na tej stronie. Próba {{ accountsTries }} z {{ accountsMaxTries }}.</span>
          </template>
        </div>

        <!-- Timeout: dane transakcyjne nadal niepobrane po wszystkich probach -->
        <div v-else-if="accountsTimedOut" class="accounts-timeout" role="status">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4m0 4h.01" stroke-linecap="round"/>
          </svg>
          <div class="accounts-timeout-body">
            <strong>Dane transakcyjne z Twojego banku nie zostały jeszcze pobrane.</strong>
            <p>Pobieranie danych może potrwać kilka minut. Nie musisz czekać na tej stronie &mdash; spróbuj ponownie
              za kilka minut z panelu klienta po zalogowaniu, wybierając „Dokończ wniosek”.</p>
            <div class="accounts-timeout-actions">
              <button type="button" class="btn-next" @click="goToPanel">
                <span class="button-content"><span>Przejdź do panelu klienta</span></span>
                <span class="button-bg"></span>
              </button>
              <button type="button" class="btn-retry" @click="retryAccounts">Sprawdź ponownie</button>
            </div>
          </div>
        </div>

        <!-- Account list -->
        <div v-else class="account-list">
          <div
            v-for="acc in accountList"
            :key="acc.id"
            class="account-card"
            :class="{ 'selected': selectedAccount === acc.iban }"
            @click="selectAccount(acc.iban)"
          >
            <div class="account-radio">
              <div class="radio-outer">
                <div class="radio-inner"></div>
              </div>
            </div>
            <div class="account-details">
              <span class="account-name">{{ acc.kontox_name || 'Rachunek bankowy' }}</span>
              <span class="account-iban">{{ formatIban(acc.iban) }}</span>
            </div>
            <div class="account-check">
              <svg v-if="selectedAccount === acc.iban" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <!-- Empty state -->
          <div v-if="accountList.length === 0 && !isAccountsLoading" class="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4m0 4h.01" stroke-linecap="round"/>
            </svg>
            <p>Nie znaleziono żadnych rachunków bankowych.</p>
          </div>
        </div>

        <!-- Error message -->
        <transition name="shake">
          <div v-if="!selectedAccount && isDirty" class="error-message">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4m0 4h.01"/>
            </svg>
            Wybierz rachunek do wypłaty pożyczki.
          </div>
        </transition>
      </div>

      <!-- Info notice -->
      <div class="info-notice">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4m0-4h.01" stroke-linecap="round"/>
        </svg>
        <p>Po wybraniu konta i zatwierdzeniu przejdziesz do podpisania umowy. Środki zostaną przelane na wybrany rachunek po podpisaniu umowy.</p>
      </div>

      <!-- Buttons (ukryte, gdy dane transakcyjne nie zostaly pobrane - akcje sa w bloku timeoutu) -->
      <div v-if="!accountsTimedOut" class="buttons-section">
        <button
          class="btn-next"
          :class="{ 'disabled': !canProceed, 'loading': isSubmitting }"
          :disabled="!canProceed || isSubmitting"
          @click="proceed"
        >
          <span class="button-content">
            <svg v-if="!isSubmitting" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14m0 0l-7-7m7 7l-7 7" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else class="btn-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <circle cx="12" cy="12" r="10" opacity="0.25"/>
              <path d="M12 2a10 10 0 0110 10" stroke-linecap="round"/>
            </svg>
            <span>{{ isSubmitting ? 'Weryfikacja...' : 'Dalej' }}</span>
          </span>
          <span class="button-bg"></span>
        </button>
      </div>

    </div>
  </div>
</template>

<script>

  // Polling listy rachunkow: po wznowieniu wniosku w statusie 0.13 'EasyCheck got'
  // dane transakcyjne z EasyCheck moga jeszcze nie byc pobrane (w testach 09.09.2026
  // trafialy do bazy 55-100 s po powrocie z EasyCheck). API zwraca wtedy 200 [] -
  // ponawiamy kilka razy, po wyczerpaniu prob odsylamy klienta do panelu.
  const ACCOUNTS_POLL_INTERVAL_MS = 10000;
  const ACCOUNTS_POLL_MAX_TRIES = 15;

  export default {

    name: 'MCAccountSelectView',

    data() {
      return {
        selectedAccount: '',
        accountList: [],
        isDirty: false,
        isSubmitting: false,
        isAccountsLoading: true,
        // polling listy rachunkow (status 0.13 'EasyCheck got' = dane transakcyjne
        // z EasyCheck moga byc jeszcze niepobrane, API zwraca wtedy 200 [])
        accountsTries: 0,
        accountsMaxTries: ACCOUNTS_POLL_MAX_TRIES,
        accountsTimedOut: false,
        accountsTimer: null,
        waitCount: 0
      }
    },

    computed: {
      canProceed() {
        return this.selectedAccount && !this.isSubmitting;
      }
    },

    methods: {

      formatIban(iban) {
        if (!iban) return '';
        const clean = iban.replace(/\s/g, '');
        return clean.replace(/(.{2})(.{2})(.{4})(.{4})(.{4})(.{4})(.{4})(.{4})/, '$1 $2 $3 $4 $5 $6 $7 $8');
      },

      selectAccount(iban) {
        if (this.isSubmitting) return;

        // LOG: wybranie konta bankowego
        this.log('ACCOUNT_SELECT', 'iban=' + iban, 'previous=' + this.selectedAccount);

        this.selectedAccount = iban;
        this.isDirty = true;
      },

      // URL listy rachunkow z analizy konta. Po wznowieniu wniosku z panelu klienta
      // sesja BFF (wnioskomat) nie ma application_id - procedura
      // getLoanCurrentStep nie zwraca wiersza dla statusu 0.13 (step 'wait'),
      // wiec resume nie zapisuje lead_id. Zalogowany klient podaje wiec
      // ireq_id w sciezce: GET /application/bank/<ireq_id>. BFF od wersji
      // 2026-09-08 sprawdza wlasnosc wniosku (cudzy/nieistniejacy -> 404) i dopiero
      // wtedy zapamietuje application_id w sesji (wczesniejsze wersje tego nie robily,
      // dlatego PATCH /client/bank dosyla id jawnie - patrz proceed()).
      // Szczegoly: temp/NOTATKA_WYBOR_KONTA_INNE_URZADZENIE.md
      bankAccountsUrl() {

        var page = this.$root.mainpage,
            leadId = page && page.loggedIn && page.leadId ? String(page.leadId).trim() : '';

        return '/application/bank' + (leadId ? '/' + encodeURIComponent(leadId) : '');
      },


      getAccounts() {

        var schedule = delay => {
              this.clearAccountsTimer();
              this.accountsTimer = setTimeout(poll, delay);
            },

            // brak rachunkow (204 / pusta tablica / wiersz bez iban) -> dane transakcyjne
            // jeszcze niepobrane: ponow albo zakoncz pollingu komunikatem
            retryOrTimeout = reason => {

              if (this.accountsTries < ACCOUNTS_POLL_MAX_TRIES) {

                this.isAccountsLoading = true;
                schedule(ACCOUNTS_POLL_INTERVAL_MS);
                return;
              }

              this.log('ACCOUNT_SELECT accounts polling TIMEOUT - client sent to panel', 'tries=' + this.accountsTries, 'reason=' + reason);

              this.isAccountsLoading = false;
              this.accountsTimedOut = true;
              this.pageTop();
            },

            poll = () => {

              this.accountsTries++;

              this.get(this.bankAccountsUrl())
                .then(response => {

                  let data = response.data,
                      list = !data ? []
                           : data.hasOwnProperty('iban') ? [data]
                           : Array.isArray(data) ? data : [];

                  // odfiltruj wiersze bez numeru rachunku (LEFT JOIN w getBankAccounts
                  // potrafi zwrocic wiersz z samymi NULL-ami)
                  list = list.filter(acc => acc && acc.iban);

                  this.log('ACCOUNT_SELECT accounts polling', 'tries=' + this.accountsTries, 'count=' + list.length, 'status=' + response.status);

                  if (list.length) {

                    this.accountList = list;
                    this.isAccountsLoading = false;
                    this.accountsTimedOut = false;
                    return;
                  }

                  retryOrTimeout('empty');
                })
                .catch(error => {

                  let status = error && error.response ? error.response.status : 0;

                  this.log('ACCOUNT_SELECT - getAccounts error', 'tries=' + this.accountsTries, 'status=' + status, error && error.message ? error.message : 'no-msg');

                  // 404 z BFF: wniosek nie istnieje albo nie nalezy do zalogowanego klienta
                  // (kontrola wlasnosci GET /application/bank/<ireq_id>) - nie krecimy
                  // pollingu, wracamy do panelu z komunikatem
                  if (status == 404) {

                    this.isAccountsLoading = false;

                    this.toast('Nie znaleziono wniosku do dokończenia. Wróć do panelu klienta i spróbuj ponownie.');

                    this.$router.push({path: '/client'});
                    return;
                  }

                  // inne bledy (timeout, 5xx, brak sesji) - traktuj jak brak danych i ponow
                  retryOrTimeout('error-' + status);
                });
            };

        this.accountsTries = 0;
        this.accountsTimedOut = false;
        this.isAccountsLoading = true;

        schedule(1000);
      },


      clearAccountsTimer() {

        this.accountsTimer && clearTimeout(this.accountsTimer);
        this.accountsTimer = null;
      },


      // "Sprawdz ponownie" po timeoucie - nowa seria prob
      retryAccounts() {

        this.log('ACCOUNT_SELECT accounts polling - manual retry');

        this.getAccounts();
      },


      // po timeoucie: zalogowany klient wraca do panelu (ponowi z "Dokoncz wniosek"),
      // niezalogowany dostaje okno logowania
      goToPanel() {

        var page = this.$root.mainpage;

        this.log('ACCOUNT_SELECT accounts timeout - go to client panel', page && page.loggedIn ? 'logged-in' : 'login-dialog');

        if (page && page.loggedIn) {
          this.$router.push({path: '/client'});
        }
        else {
          this.echo('login_dialog');
        }
      },

      proceed() {
        if (!this.selectedAccount) {
          this.isDirty = true;
          // LOG: proba kontynuacji bez wybranego konta
          this.log('ACCOUNT_SELECT_NO_SELECTION');
          return;
        }

        if (!this.canProceed) return;

        const acc = this.accountList.find(item => item.iban === this.selectedAccount);

        if (!acc) {
          this.log('ACCOUNT_SELECT - selected account not found');
          return;
        }

        // LOG: potwierdzenie wyboru konta
        this.log('ACCOUNT_SELECT_CONFIRM', 'iban=' + this.selectedAccount, 'bankId=' + acc.id, 'amount=' + this.loanAmount);

        this.isSubmitting = true;

        // id wniosku (ireq_id) dosylamy jawnie po wznowieniu z panelu, zeby zapis
        // rachunku nie zalezal od tego, czy GET listy zdazyl ustawic application_id
        // w sesji BFF (polling, odswiezenie strony, timeout sesji). W przeplywie
        // bez logowania (ten sam browser) page.leadId jest puste i BFF bierze
        // application_id z sesji jak dotad
        var page = this.$root.mainpage,
            leadId = page && page.loggedIn && page.leadId ? String(page.leadId).trim() : '',

            payload = {
              bank_id: acc.id,
              bank_account_nbr: acc.iban.replace(/[A-Z]/g, '')
            };

        leadId && (payload.loan_id = leadId);

        this.patch('/client/bank', payload)
        .then(response => {
          if (response.data) {
            let ok = !(response.data.IS_ERROR | 0);
            if (ok) {
              // LOG: sukces zapisania konta - kontynuuj polling do kroku podpisu
              this.log('ACCOUNT_SELECT_SUCCESS', 'iban=' + this.selectedAccount);

              var page = this.$root.mainpage;
              page.isLoading = 'przygotowujemy umowę do podpisu..,prosimy o cierpliwość..';
              this.waitCount = 0;

              this.onWaiting();
            } else {
              this.isSubmitting = false;
              this.log('ACCOUNT_SELECT_ERROR', 'IS_ERROR returned');
            }
          } else {
            this.isSubmitting = false;
          }
        })
        .catch(error => {
          this.isSubmitting = false;
          // LOG: blad zapisania konta
          this.log('ACCOUNT_SELECT_ERROR', error && error.message ? error.message : 'no-msg');
          if (error.response) {
            console.log(error.response);
          }
        });
      },


      onWaiting( endpoint='application' ) {

        var page = this.$root.mainpage,
            step,

          poll = () => {

            // 3 sek * 120 = 6 min
            if (this.waitCount > 120) {

              this.log('ACCOUNT_SELECT polling TIMEOUT', 'tries=' + this.waitCount);

              page.isLoading = false;
              this.isSubmitting = false;
              this.$router.push({path: 'pending'});
              return;
            }

            this.waitCount++;

            this.get( '/' + endpoint )
                .then(response => {

                  if (!!response.data) {

                    step = response.data.hasOwnProperty('step') ? response.data.step : '';

//console.log(step);

                    switch (step) {

                      case 'account':
                      case 'wait':
                        break;

                      case 'reject':
                        page.isLoading = false;
                        this.isSubmitting = false;
                        this.$router.push({path: 'rej'});
                        return;

                      case 'authologic_start':
                      case 'easycheck':
                        page.isLoading = false;
                        window.location = response.data.step_details;
                        return;

                      case 'amodit':
                        page.isLoading = false;

                        let url = response.data.step_details.split('/');

                        window.location = this.getConf('amoditSignUrl') + url[ url.length - 2 ] + '%2F' + url[ url.length - 1 ];
                        return;

                      default:
                        page.isLoading = false;
                        this.isSubmitting = false;
                        this.$router.push({path: 'thankyou'});
                        return;
                    }

                  }
                  else {
                    this.log('ACCOUNT_SELECT polling - empty body', 'tries=' + this.waitCount);
                  }

                  // dispatch another poll request
                  page.isLoading
                    && setTimeout(poll, 3000);

                })
                .catch(error => {

                    this.log('ACCOUNT_SELECT polling - request error', 'tries=' + this.waitCount, error && error.message ? error.message : 'no-msg');

                    // wait for timeout instead
                    setTimeout(poll, 3000);
                });

          };

        // initial polling
        setTimeout(poll, 3000);
      }

    },

    mounted() {
      // LOG: wejscie na strone wyboru konta
      this.log('ACCOUNT_SELECT_VIEW', 'amount=' + this.loanAmount, 'tenor=' + this.loanTenor);

      this.getAccounts();
      this.pageTop();
    },

    beforeDestroy() {

      this.accountsTimer
        && this.log('ACCOUNT_SELECT accounts polling - view destroyed before completion', 'tries=' + this.accountsTries);

      this.clearAccountsTimer();
    }
  }
</script>

<style scoped>
/* ============================================
   CSS Variables & Base (NAU green theme)
   ============================================ */
#account-select-container {
  --primary: #0bc268;
  --primary-dark: #007666;
  --primary-darker: #00605c;
  --primary-deep: #045439;
  --primary-light: #eafaf1;
  --success: #0bc268;
  --error: #ef4444;
  --text-primary: #1f2937;
  --text-secondary: #6b7280;
  --text-muted: #9ca3af;
  --bg-white: #ffffff;
  --bg-gray: #f7f9fc;
  --border: #e1e7ef;
  --border-focus: #0bc268;
  --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  --shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  --radius: 12px;
  --radius-lg: 16px;

  font-family: inherit;
  color: var(--text-primary);
  max-width: 640px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

/* ============================================
   Intro Section (Header)
   ============================================ */
.intro-section {
  text-align: center;
  padding: 2.5rem 1.5rem 2rem;
  background-image: linear-gradient(135deg, #0bc268 0%, #00605c 100%);
  border-radius: var(--radius-lg);
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;
}

.intro-section::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.intro-section::after {
  content: '';
  position: absolute;
  bottom: -30%;
  left: -10%;
  width: 200px;
  height: 200px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
}

.intro-badge {
  display: inline-block;
  padding: 0.375rem 1rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: white;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
}

.header-icon {
  width: 72px;
  height: 72px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  position: relative;
  z-index: 1;
}

.header-icon svg {
  width: 36px;
  height: 36px;
  color: white;
}

.intro-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0 0 0.5rem;
  position: relative;
  z-index: 1;
}

.intro-text {
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  position: relative;
  z-index: 1;
}

/* ============================================
   Loan Summary Card
   ============================================ */
.loan-summary-card {
  background: var(--bg-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow-sm);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid var(--border);
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-label {
  color: var(--text-secondary);
  font-size: 0.9375rem;
}

.summary-value {
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.9375rem;
}

.summary-value.highlight {
  color: var(--primary-deep);
  font-size: 1.25rem;
  font-weight: 700;
}

/* ============================================
   Account Section
   ============================================ */
.account-section {
  background: var(--bg-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.section-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.section-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  background: var(--primary-light);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-icon svg {
  width: 22px;
  height: 22px;
  color: var(--primary-dark);
}

.section-header h3 {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.25rem;
  color: var(--text-primary);
}

.section-header p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

/* Accounts Loading */
.accounts-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--text-secondary);
  gap: 1rem;
}

.loading-spinner svg {
  width: 40px;
  height: 40px;
  color: var(--primary);
  animation: spin 0.8s linear infinite;
}

.accounts-waiting-title {
  font-weight: 600;
  color: var(--text-primary);
  text-align: center;
}

.accounts-waiting-hint {
  font-size: 0.875rem;
  color: var(--text-secondary);
  text-align: center;
  max-width: 420px;
  line-height: 1.5;
}

/* Timeout pobierania danych transakcyjnych */
.accounts-timeout {
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: #fffbeb;
  border: 1px solid rgba(217, 119, 6, 0.35);
  border-radius: var(--radius);
}

.accounts-timeout > svg {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  color: #b45309;
  margin-top: 2px;
}

.accounts-timeout-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.accounts-timeout-body strong {
  color: var(--text-primary);
  font-size: 0.9375rem;
}

.accounts-timeout-body p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.5;
}

.accounts-timeout-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.btn-retry {
  height: 44px;
  padding: 0 1.25rem;
  border: 1px solid var(--primary-dark);
  border-radius: var(--radius);
  background: transparent;
  color: var(--primary-dark);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-retry:hover {
  background: var(--primary-light);
}

/* Account List */
.account-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.account-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--bg-gray);
  border: 2px solid transparent;
  border-radius: var(--radius);
  cursor: pointer;
  transition: all 0.2s ease;
}

.account-card:hover {
  background: var(--bg-white);
  border-color: var(--border);
}

.account-card.selected {
  background: var(--primary-light);
  border-color: var(--primary);
}

.account-radio {
  flex-shrink: 0;
}

.radio-outer {
  width: 22px;
  height: 22px;
  border: 2px solid var(--border);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.account-card.selected .radio-outer {
  border-color: var(--primary);
}

.radio-inner {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: transparent;
  transition: all 0.2s ease;
}

.account-card.selected .radio-inner {
  background: var(--primary);
}

.account-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.account-name {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9375rem;
}

.account-iban {
  font-size: 0.8125rem;
  color: var(--text-secondary);
  font-family: 'SF Mono', 'Menlo', monospace;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-check {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.2s ease;
}

.account-card.selected .account-check {
  opacity: 1;
  transform: scale(1);
}

.account-check svg {
  width: 24px;
  height: 24px;
  color: var(--primary-dark);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  gap: 1rem;
}

.empty-state svg {
  width: 48px;
  height: 48px;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  text-align: center;
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: #fef2f2;
  border-radius: 8px;
  color: var(--error);
  font-size: 0.875rem;
  font-weight: 500;
}

/* ============================================
   Info Notice
   ============================================ */
.info-notice {
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: var(--primary-light);
  border: 1px solid rgba(11, 194, 104, 0.25);
  border-radius: var(--radius);
  margin-bottom: 1.5rem;
}

.info-notice svg {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: var(--primary-dark);
  margin-top: 2px;
}

.info-notice p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--primary-deep);
  line-height: 1.5;
}

/* ============================================
   Buttons Section
   ============================================ */
.buttons-section {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-next {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 180px;
  height: 52px;
  padding: 0 1.75rem;
  border: none;
  border-radius: var(--radius);
  font-size: 1rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px rgba(11, 194, 104, 0.4);
}

.btn-next:hover:not(.disabled):not(.loading) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(11, 194, 104, 0.5);
}

.btn-next:active:not(.disabled):not(.loading) {
  transform: translateY(0);
}

.btn-next.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-next.loading {
  pointer-events: none;
}

.button-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-spinner {
  width: 20px;
  height: 20px;
  animation: spin 0.8s linear infinite;
}

.btn-spinner circle {
  stroke: rgba(255, 255, 255, 0.3);
}

.btn-spinner path {
  stroke: white;
}

.button-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-darker) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.btn-next:hover:not(.disabled):not(.loading) .button-bg {
  opacity: 1;
}

/* ============================================
   Animations
   ============================================ */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.shake-enter-active {
  animation: shake 0.4s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-5px); }
  40%, 80% { transform: translateX(5px); }
}

/* ============================================
   Responsive
   ============================================ */
@media (max-width: 480px) {
  #account-select-container {
    padding: 1rem;
  }

  .intro-section {
    padding: 2rem 1rem 1.5rem;
  }

  .header-icon {
    width: 60px;
    height: 60px;
  }

  .header-icon svg {
    width: 28px;
    height: 28px;
  }

  .intro-title {
    font-size: 1.375rem;
  }

  .account-section {
    padding: 1.25rem;
  }

  .account-card {
    padding: 0.875rem 1rem;
  }

  .account-iban {
    font-size: 0.75rem;
  }

  .buttons-section {
    flex-direction: column-reverse;
  }

  .btn-next {
    width: 100%;
    justify-content: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/MCAccountSelectView.vue